// NotificationCenter.tsx
import React, { useState, useEffect } from "react";
import { Form, Button, Col, Row, Card, Badge } from "react-bootstrap";
import { useTranslate } from "../hooks/LanguageProvider";
import { Bounce, toast } from "react-toastify";
import { logHistory } from "../utilities/functions";
import { useParams } from "react-router-dom";
import SelectGroup from "../components/Vehicle/SelectGroup";

const backendUrl = process.env.REACT_APP_BACKEND_URL;

interface UserInterface {
    id_user: number;
    nom_user: string;
    prenom_user: string;
}

const NotificationCenter: React.FC = () => {
    const { translate } = useTranslate();
    const { id_user } = useParams<{ id_user?: string }>();
    const user_id = localStorage.getItem("userid");

    const [Users, setUsers] = useState<UserInterface[]>([]);
    const [usersOptions, setUsersOptions] = useState<any[]>([{ value: "Aucun", label: "Aucun" }]);
    const [selectedUser, setSelectedUser] = useState<any>(null);

    const [title, setTitle] = useState<string>("");
    const [body, setBody] = useState<string>("");
    const [buttonClicked, setButtonClicked] = useState(false);

    useEffect(() => {
        getUser();
    }, []);

    useEffect(() => {
        if (id_user && usersOptions.length > 1) {
            const preselected = usersOptions.find((u) => u.value === Number(id_user));
            if (preselected) setSelectedUser(preselected);
        }
    }, [id_user, usersOptions]);

    const getUser = async () => {
        try {
            const res = await fetch(`${backendUrl}/api/notification/recipients`, { mode: "cors", credentials: "include" });
            if (!res.ok) {
                console.error("Erreur lors de la récupération des utilisateurs");
                return;
            }
            const usersData = await res.json();
            setUsers(usersData);
            const usersOptionsData = usersData.map((user: any) => ({
                value: user.id_user,
                label: `${user.nom_user} ${user.prenom_user || ""}`,
            }));
            setUsersOptions(usersOptionsData);
        } catch (error) {
            console.error("Erreur lors de la récupération des utilisateurs", error);
        }
    };

    const sendNotification = async () => {
        if (!selectedUser || title.trim() === "" || body.trim() === "") {
            toast.warn(translate("Please fill in all required fields"), {
                position: "bottom-right",
                autoClose: 2400,
                theme: "light",
                transition: Bounce,
            });
            return;
        }

        try {
            setButtonClicked(true);
            const res = await fetch(`${backendUrl}/api/notification/send`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                mode: "cors",
                credentials: "include",
                body: JSON.stringify({ target_user_id: selectedUser.value, title, body }),
            });

            const data = await res.json();

            if (!res.ok || !data.success) {
                toast.error(translate("Error while sending notification"), {
                    position: "bottom-right",
                    autoClose: 2400,
                    theme: "light",
                    transition: Bounce,
                });
            } else {
                toast.success(translate("Notification sent successfully"), {
                    position: "bottom-right",
                    autoClose: 2400,
                    theme: "light",
                    transition: Bounce,
                });

                await logHistory(
                  user_id,
                  "Send",
                  "NotificationCenter",
                  "Send notification",
                  `Send notification to user ${selectedUser.value}`
                );

                setTitle("");
                setBody("");
                if (!id_user) setSelectedUser(null);
            }
        } catch (error) {
            console.error("Erreur :", error);
            toast.error(translate("Server error"), {
                position: "bottom-right",
                autoClose: 2400,
                theme: "light",
                transition: Bounce,
            });
        } finally {
            setButtonClicked(false);
        }
    };

    const handleSubmit = (e: React.FormEvent) => e.preventDefault();

    return (
        <div style={{ width: "100%", padding: "20px" }}>
            {/* Header */}
            <Row className="mb-3">
                <Col>
                    <h4 className="d-flex align-items-center gap-2">
                        <i className="las la-bell text-primary"></i>
                        {translate("Notification Center")}
                    </h4>
                </Col>
            </Row>

            {/* Card alignée à gauche */}
            <Row>
                <Col xs={12} md={8} lg={6}>
                    <Card className="shadow-sm rounded border-0 p-4">
                        <h5 className="mb-4">{translate("Send a notification to user")}</h5>

                        <Form onSubmit={handleSubmit}>
                            {/* UTILISATEUR */}
                            <SelectGroup
                                controlId="user"
                                name="user"
                                label={translate("User")}
                                icon="user"
                                options={usersOptions}
                                valueType={selectedUser || { value: "", label: translate("Select a user") }}
                                onChange={(option: any) => setSelectedUser(option)}
                            />

                            {selectedUser && selectedUser.value !== "" && (
                                <div className="mb-3">
                                    <Badge pill bg="success">
                                        {translate("Selected")}: {selectedUser.label}
                                    </Badge>
                                </div>
                            )}

                            {/* TITRE */}
                            <Form.Group controlId="title" className="mt-3">
                                <Form.Label>{translate("Title")}</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder={translate("Enter title")}
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    required
                                    className="shadow-sm rounded"
                                />
                            </Form.Group>

                            {/* MESSAGE */}
                            <Form.Group controlId="body" className="mt-3">
                                <Form.Label>{translate("Message")}</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    placeholder={translate("Enter the message")}
                                    value={body}
                                    rows={4}
                                    onChange={(e) => setBody(e.target.value)}
                                    required
                                    className="shadow-sm rounded"
                                />
                            </Form.Group>

                            {/* BOUTONS */}
                            <div className="d-flex justify-content-start gap-2 mt-4">
                                <Button
                                    variant="primary"
                                    onClick={sendNotification}
                                    disabled={buttonClicked}
                                >
                                    {translate("Send notification")}
                                </Button>
                                <Button
                                    variant="outline-secondary"
                                    onClick={() => { setSelectedUser(null); setTitle(""); setBody(""); }}
                                >
                                    {translate("Cancel")}
                                </Button>
                            </div>
                        </Form>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default NotificationCenter;
